import { ReactNode, createContext, useState, useCallback, useMemo, useContext } from "react";

import { authFetcher } from "../hooks/useAuthSWR";

import { removeTier, removeAvailableMins } from "./account";

export interface Logout {
  (cb?: () => void): void;
}

interface ContextProps {
  isAuthenticated: boolean;
  login: (account: string, password: string, cb?: (error: any) => void) => void;
  logout: Logout;
}

interface ProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<Partial<ContextProps>>({});

const AuthProvider = ({ children }: ProviderProps): JSX.Element => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());

  const login = useCallback(async (account, password, cb) => {
    try {
      const { data } = await authFetcher("/login", {
        method: "post",
        data: {
          username: account,
          password,
        },
      });

      setToken(data.access_token);
      setIsAuthenticated(true);
      if (cb) cb(null);
    } catch (error) {
      if (cb) cb(error);
    }
  }, []);

  const logout = useCallback((cb) => {
    removeToken();
    removeTier();
    removeAvailableMins();
    setIsAuthenticated(false);
    if (cb) cb();
  }, []);

  const value = useMemo(() => ({ isAuthenticated, login, logout }), [isAuthenticated, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = (): ContextProps => useContext(AuthContext) as ContextProps;

const KEY = "act";
const getToken = (): string | null => localStorage.getItem(KEY);
const setToken = (val: string): void => localStorage.setItem(KEY, val);
const removeToken = (): void => localStorage.removeItem(KEY);

export { AuthProvider, useAuth, getToken };
